<div class="">
  <div class="max-content-width spaced-sections">
    @if (memberOnlyNotice()) {
      <div
        class="flex gap-3 rounded-r-lg border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-800 shadow-sm"
        data-testid="member-only-notice"
      >
        <div>
          <p class="font-bold">Access Restricted</p>
          <p class="mt-0.5">{{ memberOnlyNotice() }}</p>
          <a
            class="mt-2 inline-block text-xs font-semibold text-amber-700 underline hover:text-amber-900"
            href="/projects"
            >Back to Projects</a
          >
        </div>
      </div>
    }

    <!-- Error Message Alert -->
    @if (lastError()) {
      <app-base-alert
        [type]="'error'"
        [message]="lastError()!"
        [closeable]="true"
        (closed)="lastError.set(null)"
      ></app-base-alert>
    }

    <!-- PI Banners -->
    @if (isPI && hasPendingScreeningVotes() && !pendingVotesDismissed()) {
      <div
        class="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-xs flex flex-wrap items-center gap-2"
        data-testid="advance-pending-votes-info"
      >
        <span class="flex-1">
          Data Extraction can start once every researcher has completed their full-text screening
          votes. You can review active screening locks on the Project Dashboard.
        </span>
        <a
          class="font-semibold underline hover:text-blue-950"
          [routerLink]="['/projects', projectId(), 'setup']"
          >Open Dashboard</a
        >
        <button
          type="button"
          class="text-blue-400 hover:text-blue-600 transition-colors shrink-0 ml-auto p-1 rounded-lg"
          (click)="pendingVotesDismissed.set(true)"
          aria-label="Dismiss warning"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    }

    @if (isPI && hasUnresolvedConflicts() && !unresolvedConflictsDismissed()) {
      <div
        class="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs flex flex-wrap items-center gap-2"
        data-testid="advance-unresolved-conflicts-info"
      >
        <span class="flex-1">
          Data Extraction can start once all full-text screening conflicts have been resolved. There
          {{ unresolvedConflictCount() === 1 ? 'is' : 'are' }}
          {{ unresolvedConflictCount() }} unresolved conflict{{
            unresolvedConflictCount() === 1 ? '' : 's'
          }}.
        </span>
        <button
          type="button"
          class="font-semibold underline hover:text-amber-950"
          (click)="navigateToConflictResolution()"
        >
          Resolve Conflicts
        </button>
        <button
          type="button"
          class="text-amber-400 hover:text-amber-600 transition-colors shrink-0 ml-auto p-1 rounded-lg"
          (click)="unresolvedConflictsDismissed.set(true)"
          aria-label="Dismiss warning"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    }

    <!-- Main Workspace -->
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <!-- Paper / Content Area -->
      <div class="lg:col-span-3 space-y-4">
        <!-- Action Toolbar (only when paper is active) -->
        @if (activeScreeningPaper()) {
          <div class="bg-white rounded-xl border border-slate-200 shadow-sm p-2 text-sm">
            <div class="flex items-center gap-2">
              <button
                class="px-4 py-1.5 rounded-lg border font-semibold text-white bg-emerald-600 border-emerald-700 hover:bg-emerald-700 hover:border-emerald-800 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                [disabled]="submitting()"
                (click)="voteInclude()"
                title="Include paper for data extraction (I)"
                data-testid="screening-include-btn"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Include
                <kbd class="px-1 py-0.5 text-[9px] font-bold bg-white/20 rounded">I</kbd>
              </button>

              <button
                class="px-4 py-1.5 rounded-lg border font-semibold text-white bg-rose-600 border-rose-700 hover:bg-rose-700 hover:border-rose-800 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                [disabled]="submitting()"
                (click)="openExclusionPicker()"
                title="Exclude paper — select reason (E)"
                data-testid="screening-exclude-btn"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Exclude
                <kbd class="px-1 py-0.5 text-[9px] font-bold bg-white/20 rounded">E</kbd>
              </button>

              <button
                class="px-4 py-1.5 rounded-lg border border-slate-300 font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                [disabled]="submitting()"
                (click)="skipPaper()"
                title="Skip paper — postpone to end of queue (S)"
                data-testid="screening-skip-btn"
              >
                <svg
                  class="w-4 h-4 text-slate-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M13 5l7 7-7 7M5 5l7 7-7 7"
                  />
                </svg>
                Skip
                <kbd class="px-1 py-0.5 text-[9px] font-bold bg-slate-200 text-slate-600 rounded"
                  >S</kbd
                >
              </button>

              <button
                class="px-4 py-1.5 rounded-lg border border-orange-400 font-semibold text-orange-600 bg-white hover:bg-orange-50 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                [disabled]="submitting()"
                (click)="unlockCurrentPaper()"
                title="Release paper lock and return to overview"
                data-testid="screening-unlock-btn"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                  />
                </svg>
                Unlock
              </button>

              @if (paperLocked()) {
                <span
                  class="text-[10px] text-slate-400"
                  title="Locks expire after 24 hours of inactivity"
                >
                  <svg
                    class="w-4 h-4 inline -mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
              }

              <div class="ml-auto flex items-center gap-2">
                @if (votedPapersStack().length > 0) {
                  <button
                    class="px-4 py-1.5 rounded-lg border border-amber-500 font-semibold text-amber-600 bg-white hover:bg-amber-50 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    [disabled]="submitting()"
                    (click)="undoLastVote()"
                    title="Undo last vote (U)"
                    data-testid="screening-undo-btn"
                  >
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                      />
                    </svg>
                    Undo
                    <kbd
                      class="px-1 py-0.5 text-[9px] font-bold bg-amber-200 text-amber-700 rounded"
                      >U</kbd
                    >
                    <span class="text-[10px] text-amber-400 font-semibold ml-0.5"
                      >({{ votedPapersStack().length }})</span
                    >
                  </button>
                }

                @if (isPI) {
                  <app-pi-toolbar-actions
                    [unresolvedCount]="unresolvedConflictCount()"
                    [advanceDisabled]="submitting() || !canAdvanceToDataExtraction()"
                    (navigate)="navigateToConflictResolution()"
                    (advance)="onAdvanceClick()"
                  />
                }
              </div>
            </div>
          </div>
        }

        <!-- Main Content Card -->
        <div class="card flex flex-col min-h-[750px]">
          @if (isProjectReadOnly) {
            <div class="flex items-center justify-center min-h-[650px]">
              <div class="empty-state">
                <div class="empty-state-icon">🎉</div>
                <h3 class="empty-state-title">Full-Text Screening Complete!</h3>
                <p class="empty-state-message max-w-md mx-auto">
                  This project has successfully completed and advanced past the Full-Text screening
                  phase.
                </p>

                @if (stats().totalPapers > 0) {
                  <div
                    class="mt-6 flex items-center justify-center gap-6 p-4 bg-slate-50 border border-dashed border-slate-200 rounded-xl max-w-sm mx-auto"
                  >
                    <div class="text-center">
                      <span class="block text-2xl font-bold text-emerald-600">{{
                        stats().teamIncludedPapers ?? 0
                      }}</span>
                      <span class="text-xs text-slate-500 font-medium">Included</span>
                    </div>
                    <div class="w-px h-8 bg-slate-300"></div>
                    <div class="text-center">
                      <span class="block text-2xl font-bold text-rose-600">{{
                        stats().teamExcludedPapers ?? 0
                      }}</span>
                      <span class="text-xs text-slate-500 font-medium">Excluded</span>
                    </div>
                  </div>
                }

                <p class="text-xs text-slate-400 mt-4">
                  Note: This workspace is in read-only mode because the screening stage is closed.
                </p>
              </div>
            </div>
          } @else if (!activeScreeningPaper() && !hasRequestedFirstPaper()) {
            <div class="flex flex-col items-center justify-center min-h-[650px] text-center">
              <div class="empty-state-icon">
                <svg
                  class="w-12 h-12 mx-auto text-secondary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-800 mt-4">Start Screening</h3>
              <p class="text-sm text-slate-500 max-w-sm mx-auto mt-2">
                Pull the next paper from the queue. It will be locked to you until you vote, skip or
                unlock.
              </p>
              <button
                (click)="loadNextScreeningPaper()"
                class="mt-6 px-6 py-3 rounded-lg font-bold text-white bg-secondary hover:bg-hover transition-colors shadow-lg shadow-secondary/20 inline-flex items-center gap-2 text-base"
              >
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Start Screening
              </button>
            </div>
          } @else if (!activeScreeningPaper()) {
            <div class="flex items-center justify-center min-h-[650px]">
              <div class="empty-state">
                <div class="empty-state-icon">🎉</div>
                @if (stats().partiallyScreenedPapers > 0) {
                  <h3 class="empty-state-title">Your Queue is Clear</h3>
                  <p class="empty-state-message max-w-md mx-auto">
                    You have screened all your assigned papers. Some papers still await votes from
                    other team members.
                  </p>
                } @else {
                  <h3 class="empty-state-title">Screening Completed!</h3>
                  <p class="empty-state-message max-w-md mx-auto">
                    All papers in the screening queue have been successfully processed.
                  </p>
                  <p class="text-xs text-slate-400 mt-4">
                    @if (isPI) {
                      Use the <strong>Advance to Data Extraction</strong> button in the toolbar to
                      transition the project phase.
                    } @else {
                      The project is ready to be advanced to the Data Extraction phase by the
                      Principal Investigator.
                    }
                  </p>
                }

                @if (stats().totalPapers > 0) {
                  <div
                    class="mt-6 flex items-center justify-center gap-6 p-4 bg-slate-50 border border-dashed border-slate-200 rounded-xl max-w-sm mx-auto"
                  >
                    <div class="text-center">
                      <span class="block text-2xl font-bold text-emerald-600">{{
                        stats().teamIncludedPapers ?? 0
                      }}</span>
                      <span class="text-xs text-slate-500 font-medium">Team Included</span>
                    </div>
                    <div class="w-px h-8 bg-slate-300"></div>
                    <div class="text-center">
                      <span class="block text-2xl font-bold text-rose-600">{{
                        stats().teamExcludedPapers ?? 0
                      }}</span>
                      <span class="text-xs text-slate-500 font-medium">Team Excluded</span>
                    </div>
                  </div>
                }

                <div class="mt-6 flex items-center justify-center gap-2">
                  @if (votedPapersStack().length > 0) {
                    <button
                      class="px-4 py-1.5 rounded-lg border border-amber-500 font-semibold text-amber-600 bg-white hover:bg-amber-50 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      [disabled]="submitting()"
                      (click)="undoLastVote()"
                      data-testid="screening-undo-btn"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                        />
                      </svg>
                      Undo
                      <kbd
                        class="px-1 py-0.5 text-[9px] font-bold bg-amber-200 text-amber-700 rounded"
                        >U</kbd
                      >
                    </button>
                  }

                  @if (isPI) {
                    <app-pi-toolbar-actions
                      [unresolvedCount]="unresolvedConflictCount()"
                      [advanceDisabled]="submitting() || !canAdvanceToDataExtraction()"
                      (navigate)="navigateToConflictResolution()"
                      (advance)="onAdvanceClick()"
                    />
                  }
                </div>
              </div>
            </div>
          } @else {
            <div class="flex-1 overflow-hidden relative rounded-xl bg-[#D4D4D7]">
              <app-cad-pdf-viewer
                class="absolute inset-0 -mt-0.5"
                (loadFailed)="onViewerLoadFailed()"
              />
            </div>
          }
        </div>
      </div>

      <!-- Sidebar: Progress + Agreement Stats -->
      <div class="lg:col-span-1 flex flex-col gap-4">
        <div>
          <app-screening-progress-card
            [totalPapers]="stats().totalPapers"
            [unscreenedPapers]="stats().unscreenedPapers"
            [partiallyScreenedPapers]="stats().partiallyScreenedPapers"
            [includedPapers]="stats().includedPapers"
            [excludedPapers]="stats().excludedPapers"
            [isPI]="isPI"
            [researcherProgress]="stats().researcherProgress ?? []"
            [teamTotalDecisions]="stats().teamTotalDecisions ?? 0"
            [teamTotalRequiredDecisions]="stats().teamTotalRequiredDecisions ?? 0"
          ></app-screening-progress-card>
        </div>

        @if (activeScreeningPaper(); as paper) {
          <div>
            <div class="bg-white rounded-xl p-4 shadow-sm border border-slate-200/60">
              <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                Active Paper
              </h3>
              <h4 class="text-sm font-semibold text-slate-800 leading-snug mb-1">
                {{ paper.title }}
              </h4>
              @if (paper.authors) {
                <p class="text-xs text-slate-500">{{ paper.authors }}</p>
              }
            </div>
          </div>
        }

        <div>
          <app-agreement-stats-card
            [visible]="isPI"
            [projectId]="projectId()"
            [stage]="ProjectState.FULL_TEXT_SCREENING"
            [projectState]="currentProjectState"
            [compact]="true"
          ></app-agreement-stats-card>
        </div>
      </div>
    </div>

    <!-- Screening Criteria Bar -->
    @if (project) {
      <app-screening-criteria-bar
        [inclusionCriteria]="project?.inclusionCriteria"
        [exclusionCriteria]="project?.exclusionCriteria"
      />
    }
  </div>
</div>

<!-- Advance Confirmation Modal -->
<app-confirm-modal
  [open]="advanceConfirmOpen()"
  title="Advance to Data Extraction?"
  message="This locks full-text screening and moves the project to Data Extraction. This cannot be undone."
  confirmText="Advance"
  [confirmVariant]="'primary'"
  [loading]="submitting()"
  (openChange)="onAdvanceConfirmOpenChange($event)"
  (confirmed)="advanceProject()"
></app-confirm-modal>

<!-- Exclusion Reason Modal -->
@if (showExclusionPicker()) {
  <div
    class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] transition-all"
    data-testid="exclusion-modal"
  >
    <div
      class="bg-white rounded-xl shadow-xl border border-slate-200/80 w-[420px] max-w-[90vw] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200"
    >
      <div class="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <h3 class="font-bold text-slate-800 text-sm">Select Exclusion Reason</h3>
        <button
          class="text-slate-400 hover:text-slate-600"
          (click)="showExclusionPicker.set(false)"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div class="p-4 max-h-[300px] overflow-y-auto flex flex-col gap-2">
        @if (exclusionReasons().length === 0) {
          <p class="text-xs text-slate-400 text-center py-4">
            No active exclusion reasons configured for this project.
          </p>
        } @else {
          @for (reason of exclusionReasons(); track reason) {
            <button
              class="w-full text-left p-3 rounded-lg border border-slate-200/80 hover:border-rose-400 hover:bg-rose-50/20 text-xs font-semibold text-slate-700 transition-all flex items-center justify-between group"
              (click)="voteExclude(reason)"
              data-testid="exclusion-reason-item"
            >
              <span>{{ reason }}</span>
              <svg
                class="w-4 h-4 text-slate-400 group-hover:text-rose-500 transition-colors"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          }
        }
      </div>

      <div class="p-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
        <button
          class="px-4 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-600 bg-white hover:bg-slate-50 transition-colors shadow-sm"
          (click)="showExclusionPicker.set(false)"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
}
